import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  toast,
} from "@bfc-software/pallet";
import { Dialog, Modal as RACModal, ModalOverlay } from "react-aria-components";
import { TFunction } from "i18next";
import { Check, Edit2, Info, X } from "react-feather";
import { logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";
import modalStyles from "@bfc-software/pallet/components/structure/modal.module.css";

import styles from "./zones.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/zones",
)({
  component: RouteComponent,
});

type Zone = {
  id: number;
  name: string;
  abbreviation?: string | null;
  description?: string | null;
  product_type_ids: number[];
};

type ProductType = {
  id: number;
  name: string;
};

/**
 * A zone's identity color -- used for its card's left border and its product
 * type chips, so a zone stays visually recognizable as its chips reappear on
 * other cards while being edited. Cycled by id rather than name so a renamed
 * zone keeps its color.
 */
const ZONE_COLORS = [
  "#2f6fb2",
  "#c47f12",
  "#0aa3a0",
  "#7b61c9",
  "#cf2b22",
  "#3d8b3d",
];

function zoneColor(id: number): string {
  return ZONE_COLORS[id % ZONE_COLORS.length]!;
}

const VOWELS = new Set(["A", "E", "I", "O", "U"]);

/**
 * The abbreviation field's default -- the zone's first 3 consonants, e.g.
 * "Produce" -> "PRD". Recomputed as the name changes until the user edits
 * the field directly, at which point their value takes over.
 */
function firstConsonants(name: string, count: number): string {
  const consonants: string[] = [];
  for (const char of name.toUpperCase()) {
    if (consonants.length >= count) break;
    if (char >= "A" && char <= "Z" && !VOWELS.has(char)) consonants.push(char);
  }
  return consonants.join("");
}

/**
 * openapi-react-query throws the parsed ErrorResponse body itself
 * (`{ error, message }`) rather than an `Error`, so `instanceof Error` alone
 * would miss it -- see the analogous helper in items.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * The zone creation form -- an inline panel injected under the "Create zone"
 * button rather than a modal, so picking product types stays in the same view
 * as the rest of the zone list.
 */
function CreateZonePanel({
  productTypes,
  isAssigned,
  onSubmit,
  onClose,
  t,
}: {
  productTypes: ProductType[];
  isAssigned: (productTypeId: number) => boolean;
  onSubmit: (values: {
    name: string;
    abbreviation: string;
    description: string;
    productTypeIds: number[];
  }) => Promise<void>;
  onClose: () => void;
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [abbreviationEdited, setAbbreviationEdited] = useState(false);
  const [productTypeIds, setProductTypeIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableTypes = productTypes.filter((pt) => !isAssigned(pt.id));

  const toggleType = (id: number) => {
    setProductTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!abbreviationEdited) setAbbreviation(firstConsonants(value, 3));
  };

  const handleAbbreviationChange = (value: string) => {
    const next = value.toUpperCase().slice(0, 3);
    setAbbreviation(next);
    // An emptied field resumes auto-fill from the name rather than staying
    // stuck on a blank value the user backed out of.
    setAbbreviationEdited(next.length > 0);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || name.trim().length === 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit({ name, abbreviation, description, productTypeIds });
      onClose();
    } catch {
      // onSubmit already reported the failure via toast; keep the panel open
      // so the user can retry without re-entering values.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={styles.panel}
      data-test-role="create-zone-panel"
    >
      <div className={styles.panelTitle}>{t("zones.form.createTitle")}</div>

      <div className={styles.panelGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t("zones.form.nameLabel")}</span>
          <input
            className={styles.input}
            value={name}
            onChange={(ev) => handleNameChange(ev.target.value)}
            placeholder={t("zones.form.namePlaceholder")}
            autoFocus={true}
            required={true}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t("zones.form.abbreviationLabel")}
          </span>
          <input
            className={`${styles.input} ${styles.abbreviationInput}`}
            value={abbreviation}
            onChange={(ev) => handleAbbreviationChange(ev.target.value)}
            placeholder={t("zones.form.abbreviationPlaceholder")}
            maxLength={3}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t("zones.form.descriptionLabel")}
          </span>
          <input
            className={styles.input}
            value={description}
            onChange={(ev) => setDescription(ev.target.value)}
            placeholder={t("zones.form.descriptionPlaceholder")}
          />
        </label>
      </div>

      <div className={styles.typesLabel}>
        {t("zones.form.productTypesLabel")}
      </div>
      <div className={styles.pillRow}>
        {availableTypes.map((pt) => {
          const selected = productTypeIds.includes(pt.id);
          return (
            <button
              type="button"
              key={pt.id}
              className={selected ? styles.pillSelected : styles.pill}
              aria-pressed={selected}
              onClick={() => toggleType(pt.id)}
            >
              <span className={styles.pillCheck}>
                {selected ? <Check size={11} strokeWidth={3} /> : null}
              </span>
              {pt.name}
            </button>
          );
        })}
        {availableTypes.length === 0 ? (
          <p className={styles.typesEmpty}>{t("zones.form.noProductTypes")}</p>
        ) : null}
      </div>

      <div className={styles.panelActions}>
        <Button
          type="submit"
          text={isSubmitting ? t("zones.form.saving") : t("zones.form.submit")}
          isDisabled={isSubmitting || name.trim().length === 0}
        />
        <Button
          type="button"
          variant="tertiary"
          text={t("zones.form.cancel")}
          onPress={onClose}
          isDisabled={isSubmitting}
        />
      </div>
    </form>
  );
}

/**
 * The zone's location count, via ISS's `/slots/list?zoneName=` filter --
 * `limit: 1` so only `pagination.total_count` is paid for, not the rows
 * themselves (the same low-cost-count pattern items.tsx uses for its
 * attribute filter counts).
 *
 * Its own component rather than a query mapped over the zone list: the
 * number of zones changes between renders, and calling a hook once per zone
 * inside `.map()` in the parent would violate the rules of hooks.
 */
function ZoneLocationsCount({
  zoneName,
  client,
}: {
  zoneName: string;
  client: ReturnType<typeof apiClient>;
}) {
  const query = client.useQuery("get", "/slots/list", {
    params: { query: { zoneName, limit: 1 } },
  });

  if (query.isLoading) return <Loader type="inline" />;
  if (query.isError) return <span className={styles.locationsValue}>—</span>;
  return (
    <span className={styles.locationsValue}>
      {query.data?.pagination.total_count ?? 0}
    </span>
  );
}

function ZoneCard({
  zone,
  client,
  productTypeName,
  availableToAdd,
  addOpen,
  onToggleAdd,
  onAddType,
  onRemoveType,
  onDelete,
  t,
}: {
  zone: Zone;
  client: ReturnType<typeof apiClient>;
  productTypeName: Map<number, string>;
  availableToAdd: ProductType[];
  addOpen: boolean;
  onToggleAdd: () => void;
  onAddType: (productTypeId: number) => void;
  onRemoveType: (productTypeId: number) => void;
  onDelete: () => void;
  t: TFunction;
}) {
  const color = zoneColor(zone.id);

  return (
    <div
      className={styles.card}
      style={{ borderLeftColor: color }}
      data-test-role="zone-row"
    >
      <div className={styles.cardNameCol}>
        <div className={styles.cardHeader}>
          <span className={styles.cardName} data-test-role="zone-name">
            {zone.name}
          </span>
          <span className={styles.codeBadge} data-test-role="zone-abbreviation">
            {zone.abbreviation || "—"}
          </span>
        </div>
        {zone.description ? (
          <p className={styles.cardDescription}>{zone.description}</p>
        ) : null}
      </div>

      <div className={styles.cardLocationsCol}>
        <div className={styles.typesLabel}>{t("zones.locationsLabel")}</div>
        <ZoneLocationsCount zoneName={zone.name} client={client} />
      </div>

      <div className={styles.cardTypesCol}>
        <div className={styles.typesLabel}>{t("zones.productTypesLabel")}</div>
        <div className={styles.chipRow}>
          {zone.product_type_ids.map((id) => (
            <span
              key={id}
              className={styles.chip}
              style={{ color, backgroundColor: `${color}1e` }}
            >
              {productTypeName.get(id) ?? id}
              {addOpen ? (
                <button
                  type="button"
                  className={styles.chipRemove}
                  style={{ color }}
                  aria-label={t("zones.rowActions.removeType")}
                  onClick={() => onRemoveType(id)}
                >
                  <X size={12} />
                </button>
              ) : null}
            </span>
          ))}
          {zone.product_type_ids.length === 0 ? (
            <span className={styles.noTypes}>
              {t("zones.noProductTypesAssigned")}
            </span>
          ) : null}
        </div>

        {addOpen ? (
          <div className={styles.addRow}>
            {availableToAdd.map((pt) => (
              <button
                type="button"
                key={pt.id}
                className={styles.addPill}
                onClick={() => onAddType(pt.id)}
              >
                + {pt.name}
              </button>
            ))}
            {availableToAdd.length === 0 ? (
              <span className={styles.typesEmpty}>
                {t("zones.form.noProductTypes")}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={styles.cardActions}>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={
            addOpen
              ? t("zones.rowActions.doneEditing")
              : t("zones.rowActions.edit")
          }
          onClick={onToggleAdd}
        >
          {addOpen ? <Check size={15} /> : <Edit2 size={14} />}
        </button>
        <Button
          variant="tertiary"
          icon={{ type: "trash" }}
          aria-label={t("zones.rowActions.delete")}
          onPress={onDelete}
        />
      </div>
    </div>
  );
}

function DeleteZoneConfirm({
  zone,
  isPending,
  onCancel,
  onConfirm,
  t,
}: {
  zone: Zone | undefined;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: TFunction;
}) {
  return (
    <ModalOverlay
      className={modalStyles.modalOverlay}
      isDismissable={!isPending}
      isOpen={zone !== undefined}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <RACModal className={modalStyles.modal}>
        <Dialog
          className={modalStyles.dialog}
          data-test-role="delete-zone-confirm"
        >
          <div className={styles.deleteConfirm}>
            <h2>{t("zones.deleteConfirm.title")}</h2>
            <p className={styles.deleteConfirmBody}>
              {t("zones.deleteConfirm.body")}
            </p>
            {zone ? (
              <p className={styles.deleteConfirmTarget}>{zone.name}</p>
            ) : null}
          </div>
          <div className={styles.modalActions}>
            <Button
              text={t("zones.deleteConfirm.cancel")}
              variant="tertiary"
              onPress={onCancel}
              isDisabled={isPending}
            />
            <Button
              text={t("zones.deleteConfirm.confirm")}
              variant="danger"
              onPress={onConfirm}
              isDisabled={isPending}
            />
          </div>
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
}

export function RouteComponent() {
  const { lang } = Route.useParams();
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useMemo(() => apiClient(), []);

  const zonesQuery = client.useQuery("get", "/zones/all", {});
  const productTypesQuery = client.useQuery("get", "/product-types/all", {});

  const createMutation = client.useMutation("post", "/zones/create");
  const updateMutation = client.useMutation("post", "/zones/update");
  const deleteMutation = client.useMutation("post", "/zones/delete");

  const [createOpen, setCreateOpen] = useState(false);
  const [addOpenFor, setAddOpenFor] = useState<number | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Zone | undefined>();

  const zones = (zonesQuery.data?.data ?? []) as Zone[];
  const productTypes = (productTypesQuery.data?.data ?? []) as ProductType[];

  const zoneOf = new Map<number, Zone>();
  zones.forEach((zone) =>
    zone.product_type_ids.forEach((id) => zoneOf.set(id, zone)),
  );
  const productTypeName = new Map(productTypes.map((pt) => [pt.id, pt.name]));
  const unassignedTypes = productTypes.filter((pt) => !zoneOf.has(pt.id));

  const isLoading = zonesQuery.isLoading || productTypesQuery.isLoading;
  const isError = zonesQuery.isError || productTypesQuery.isError;

  const refetchAll = () => {
    zonesQuery.refetch();
    productTypesQuery.refetch();
  };

  const handleCreateSubmit = async (values: {
    name: string;
    abbreviation: string;
    description: string;
    productTypeIds: number[];
  }) => {
    try {
      const created = await createMutation.mutateAsync({
        body: {
          name: values.name.trim(),
          abbreviation: values.abbreviation.trim() || null,
          description: values.description.trim() || null,
          product_type_ids: values.productTypeIds,
        },
      });
      logger.info("Created zone", { id: created.data.id });
      toast.success(t("zones.form.createSuccessToast"));
      refetchAll();
    } catch (error) {
      logger.warn("Failed to create zone", { error });
      toast.error(errorMessage(error, t("zones.form.saveFailedFallback")));
      throw error;
    }
  };

  const updateZoneTypes = (zone: Zone, nextIds: number[]) => {
    updateMutation
      .mutateAsync({
        params: { query: { zoneId: zone.id } },
        body: {
          name: zone.name,
          abbreviation: zone.abbreviation ?? null,
          description: zone.description ?? null,
          product_type_ids: nextIds,
        },
      })
      .then(() => {
        logger.info("Updated zone product types", { id: zone.id });
        refetchAll();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to update zone product types", {
          error,
          id: zone.id,
        });
        toast.error(errorMessage(error, t("zones.form.saveFailedFallback")));
      });
  };

  const closeDeleteConfirm = () => {
    if (deleteMutation.isPending) return;
    setDeleteTarget(undefined);
  };

  const confirmDelete = () => {
    if (!deleteTarget || deleteMutation.isPending) return;

    const target = deleteTarget;
    deleteMutation
      .mutateAsync({ params: { query: { zoneId: target.id } } })
      .then(() => {
        logger.info("Deleted zone", { id: target.id });
        toast.success(t("zones.deleteConfirm.successToast"));
        setDeleteTarget(undefined);
        refetchAll();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to delete zone", { error, id: target.id });
        toast.error(errorMessage(error, t("zones.deleteConfirm.errorToast")));
      });
  };

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={styles.page}>
        <header className={styles.header}>
          <h1>{t("zones.title")}</h1>
          <p className={styles.subtitle}>{t("zones.subtitle")}</p>
        </header>

        {isLoading ? (
          <Loader type="page" />
        ) : isError ? (
          <AppError
            description={t("zones.failed")}
            resolution={t("zones.failureSupport")}
          />
        ) : (
          <>
            <div className={styles.infoBanner}>
              <Info size={18} className={styles.infoIcon} aria-hidden={true} />
              <span className={styles.infoNote}>
                {t("zones.setupNote", { count: zones.length })}
              </span>
              {unassignedTypes.length > 0 ? (
                <span className={styles.unassignedBadge}>
                  {t("zones.unassignedBadge", {
                    count: unassignedTypes.length,
                  })}
                </span>
              ) : null}
            </div>

            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleRow}>
                <h2 className={styles.sectionTitle}>
                  {t("zones.listHeading")}
                </h2>
                <span className={styles.countBadge}>{zones.length}</span>
              </div>
              <Button
                className={styles.createButton}
                icon={{ type: "plus" }}
                text={t("zones.newButton")}
                onPress={() => setCreateOpen(true)}
              />
            </div>

            {createOpen ? (
              <CreateZonePanel
                productTypes={productTypes}
                isAssigned={(id) => zoneOf.has(id)}
                onSubmit={handleCreateSubmit}
                onClose={() => setCreateOpen(false)}
                t={t}
              />
            ) : null}

            {zones.length === 0 ? (
              <EmptyState heading={t("zones.empty")} />
            ) : (
              <div className={styles.cardsList}>
                {zones.map((zone) => (
                  <ZoneCard
                    key={zone.id}
                    zone={zone}
                    client={client}
                    productTypeName={productTypeName}
                    availableToAdd={unassignedTypes}
                    addOpen={addOpenFor === zone.id}
                    onToggleAdd={() =>
                      setAddOpenFor((prev) =>
                        prev === zone.id ? undefined : zone.id,
                      )
                    }
                    onAddType={(typeId) =>
                      updateZoneTypes(zone, [...zone.product_type_ids, typeId])
                    }
                    onRemoveType={(typeId) =>
                      updateZoneTypes(
                        zone,
                        zone.product_type_ids.filter((id) => id !== typeId),
                      )
                    }
                    onDelete={() => setDeleteTarget(zone)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <DeleteZoneConfirm
        zone={deleteTarget}
        isPending={deleteMutation.isPending}
        onCancel={closeDeleteConfirm}
        onConfirm={confirmDelete}
        t={t}
      />
    </div>
  );
}
